import { MuteText } from '@elements/Common';
import { useComponentsMetadata } from '@hooks/components';
import { CommonUtility } from '@utility/common';
import { cloneElement, isValidElement, type ReactNode } from 'react';
import type { IItemAttributes } from 'types/shipment/types';

function Row({ children }: { children: ReactNode }) {
  return (
    <div className="sk:mb-1.25 sk:flex sk:items-center sk:justify-between sk:gap-2">
      {children}
    </div>
  );
}

interface IProductPropertiesProps {
  attributes: IItemAttributes;
  isMultiple: boolean;
  AliasRenderer?: React.ReactNode;
  showAlias?: boolean;
  imperial?: boolean; // for length and weight form
  unitText?: string;
}

export function ProductProperties({
  attributes,
  isMultiple,
  AliasRenderer,
  showAlias = true,
  unitText = '',
  imperial = false,
}: IProductPropertiesProps) {
  const { options: optionsComponentsMetadata } = useComponentsMetadata();

  const renderUnitsPerCarton = () => {
    if (isMultiple) {
      return '-';
    }
    if (attributes?.units_per_carton && attributes?.unit_of_measurement) {
      const unitsPerCarton = CommonUtility.getUnitRenderInfo({
        isImperial: imperial,
        optionsComponentsMetadata,
        unit: attributes.unit_of_measurement,
        value: attributes.units_per_carton,
        group: undefined,
      });

      return (
        <>
          {CommonUtility.numberWithCommas(unitsPerCarton.value)}{' '}
          <MuteText>{unitsPerCarton.unit}</MuteText>
        </>
      );
    }
    if (attributes?.units_per_carton) {
      return (
        <>
          {CommonUtility.numberWithCommas(attributes?.units_per_carton)}
          <MuteText> {unitText}</MuteText>
        </>
      );
    }
    return '-';
  };

  return (
    <>
      {showAlias ? (
        <Row>
          <MuteText className="sk:w-17.5 sk:text-left sk:whitespace-nowrap">
            Alias
          </MuteText>
          <div className="sk:text-right">
            {!attributes?.alias ? (
              '-'
            ) : (
              <span>
                {isValidElement(AliasRenderer)
                  ? cloneElement(AliasRenderer)
                  : isMultiple
                    ? 'Multiple'
                    : attributes.alias}
              </span>
            )}
          </div>
        </Row>
      ) : null}
      <Row>
        <MuteText className="sk:text-left">Carton Size</MuteText>
        <div className="sk:text-right">
          {!attributes?.carton_length ? (
            '-'
          ) : (
            <span>
              {isMultiple ? (
                '-'
              ) : (
                <>
                  {CommonUtility.ctnSize(attributes)}{' '}
                  <MuteText>
                    {attributes?.carton_height?.unit === 'inch'
                      ? 'in.'
                      : attributes?.carton_height?.unit}
                  </MuteText>
                </>
              )}
            </span>
          )}
        </div>
      </Row>
      <Row>
        <MuteText className="sk:text-left">Quantity Per Carton</MuteText>
        <div className="sk:text-right">{renderUnitsPerCarton()}</div>
      </Row>
      <Row>
        <MuteText className="sk:text-left">Gross Weight (Carton)</MuteText>
        <div className="sk:text-right">
          {isMultiple
            ? '-'
            : CommonUtility.roundNumber(
                attributes?.carton_gross_weight?.value,
              ) || ''}{' '}
          <MuteText>
            {isMultiple ? '' : attributes?.carton_gross_weight?.unit || '-'}
          </MuteText>
        </div>
      </Row>
    </>
  );
}
